import { lowerCamelCase } from '@art-suite/art-core-ts';
import { FloorPlanRoom, measureFloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { ScanValue } from '../ScanRoot';
import { ScanGroupOptimized } from '../ScanRootOptimized';
import { ScanTemplateGroup, ScanTemplateRoot, ScanTemplateValues } from '../ScanTemplateRoot';
import { STANDARD_ROOM_GROUP_VALUES } from './StandardScanTemplateSchemas';

const getSortedUniqueStrings = (values: string[]): string[] => [...new Set(values)].sort((a, b) => a.localeCompare(b));
/**
 * Returns the value names in the given values-map that are tagged as flooring area measurements.
 */
export const getFlooringAreaValueNames = (values?: ScanTemplateValues): string[] =>
  Object.keys(values ?? {}).filter(name => values?.[name]?.meaningType === 'flooringAreaMeasurement');

/**
 * Returns the value names in the given values-map that are tagged as flooring area measurements.
 */
export const getGroupFlooringAreaValueNames = (group: ScanTemplateGroup): string[] => getFlooringAreaValueNames(group.values);

/**
 * Returns all unique flooring-area value names used across all groups in the template, sorted.
 * Useful for guiding users toward consistent flooring value names across groups.
 */
export const getAllFlooringAreaValueNames = (scanTemplateRoot: ScanTemplateRoot): string[] =>
  getSortedUniqueStrings(scanTemplateRoot.groupTemplates?.flatMap(getGroupFlooringAreaValueNames) ?? []) ?? [];

/**
 * Normalizes user input into a flooring-area value name: lowerCamelCase, always ending in "Area".
 * Examples: "hardwood" -> "hardwoodArea", "Tile Area" -> "tileArea"
 */
export const normalizeFlooringAreaValueName = (name: string): string => {
  const normalized = lowerCamelCase(name.trim());
  if (!normalized) return '';
  return normalized.endsWith('Area') ? normalized : `${normalized}Area`;
};

/**
 * Standard room value names ending in "Area" (roomArea, roomWallArea) are reserved —
 * they must not be used as flooring-area value names. Enforced in the UX, not the schema.
 */
export const RESERVED_FLOORING_AREA_VALUE_NAMES: string[] = Object.keys(STANDARD_ROOM_GROUP_VALUES).filter(name => name.endsWith('Area'));

export const isReservedFlooringAreaValueName = (name: string): boolean => RESERVED_FLOORING_AREA_VALUE_NAMES.includes(name);

/**
 * The complete group update for a floor plan edit, shaped as a ready
 * `updateGroup` payload: `updateGroup(group.id, getGroupUpdatesFromFloorPlanRoom(group, room))`.
 * Always persist it whole — never cherry-pick values from it.
 */
export const getGroupUpdatesFromFloorPlanRoom = (
  group: ScanGroupOptimized,
  room: FloorPlanRoom,
): { roomFloorPlan: FloorPlanRoom; values: Record<string, ScanValue> } => {
  const measurements = measureFloorPlanRoom(room);
  const areaByType = measurements.areaByType;
  const flooringAreaValueNames = getGroupFlooringAreaValueNames(group.template);
  const mergeValue = (
    valueKey: string,
    total: number,
  ): ScanValue => ({
    ...(group.values[valueKey] ?? {}),
    setValue: total,
  });

  // Every flooring-area value gets updated: unpainted types are zeroed so
  // removing a floor type from the plan never leaves a stale saved area.
  const flooringAreaUpdates: Record<string, ScanValue> = {};

  for (const valueName of flooringAreaValueNames) {
    const paintedArea = areaByType.find(area => area.type === valueName)?.area ?? 0;
    flooringAreaUpdates[valueName] = mergeValue(valueName, paintedArea);
  }

  return {
    roomFloorPlan: room,
    values: {
      ...flooringAreaUpdates,
      roomArea: mergeValue('roomArea', measurements.totalFloorArea),
      roomPerimeter: mergeValue('roomPerimeter', measurements.perimeter),
      roomWallArea: mergeValue('roomWallArea', measurements.wallArea),
      roomBaseboardLength: mergeValue('roomBaseboardLength', measurements.baseboardLength),
      roomCrownLength: mergeValue('roomCrownLength', measurements.crownLength),
      roomDoorTrimLength: mergeValue('roomDoorTrimLength', measurements.doorTrimLength),
      roomWindowTrimLength: mergeValue('roomWindowTrimLength', measurements.windowTrimLength),
      roomWindowSillLength: mergeValue('roomWindowSillLength', measurements.windowSillLength),
      roomCountertopArea: mergeValue('roomCountertopArea', measurements.countertopArea),
    },
  };
};
